export interface Vec3 { x: number; y: number; z: number }
export interface Quat { x: number; y: number; z: number; w: number }

export const vec = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const add = (a: Vec3, b: Vec3): Vec3 => vec(a.x + b.x, a.y + b.y, a.z + b.z);
export const sub = (a: Vec3, b: Vec3): Vec3 => vec(a.x - b.x, a.y - b.y, a.z - b.z);
export const scale = (a: Vec3, s: number): Vec3 => vec(a.x * s, a.y * s, a.z * s);
export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a: Vec3, b: Vec3): Vec3 =>
  vec(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
export const length = (a: Vec3): number => Math.sqrt(dot(a, a));
export const normalize = (a: Vec3): Vec3 => {
  const l = length(a);
  return l > 1e-9 ? scale(a, 1 / l) : vec();
};
export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

export const quatIdentity = (): Quat => ({ x: 0, y: 0, z: 0, w: 1 });

export const quatMul = (a: Quat, b: Quat): Quat => ({
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
});

const conj = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: q.w });

const quatNormalize = (q: Quat): Quat => {
  const l = Math.hypot(q.x, q.y, q.z, q.w);
  return { x: q.x / l, y: q.y / l, z: q.z / l, w: q.w / l };
};

export const quatFromAxisAngle = (axis: Vec3, angle: number): Quat => {
  const s = Math.sin(angle / 2);
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(angle / 2) };
};

export const rotate = (q: Quat, v: Vec3): Vec3 => {
  const p = quatMul(quatMul(q, { ...v, w: 0 }), conj(q));
  return vec(p.x, p.y, p.z);
};

export const rotateInv = (q: Quat, v: Vec3): Vec3 => rotate(conj(q), v);

/** Advance orientation by world-space angular velocity w over dt. */
export const integrateQuat = (q: Quat, w: Vec3, dt: number): Quat => {
  const d = quatMul({ x: w.x, y: w.y, z: w.z, w: 0 }, q);
  return quatNormalize({
    x: q.x + 0.5 * dt * d.x,
    y: q.y + 0.5 * dt * d.y,
    z: q.z + 0.5 * dt * d.z,
    w: q.w + 0.5 * dt * d.w,
  });
};
